import { estEchafaudage, REFUS_ACTIVATION, type OrigineEchafaudage } from './echafaudage.js';

/**
 * CE QU'ON PEUT FAIRE SUR CETTE PARCELLE-CI — ET POURQUOI PAS LE RESTE.
 *
 * ⚠️⚠️ TROIS FILTRES, ET L'ORDRE DÉCIDE DE CE QU'ON DIT À L'AGENT.
 *
 *   1. l'OBJET   — cette parcelle se prête-t-elle à ce geste ?
 *   2. le MÉTIER — ce geste est-il de votre ressort ?
 *   3. l'HABILITATION — votre rôle l'autorise-t-il ?
 *
 * Les trois produisent une absence, et les trois absences ne veulent pas dire
 * la même chose. « Ce n'est pas pour vous », « demandez l'habilitation » et
 * « cette parcelle ne s'y prête pas encore » appellent trois gestes opposés.
 * Un écran qui les fond en un bouton grisé — ou pire, en un bouton absent —
 * force l'agent à deviner lequel des trois, et il devine mal.
 *
 * ⚠️ UNE ACTION NE DISPARAÎT JAMAIS EN SILENCE. Si elle est du ressort du
 * métier, elle est LISTÉE : soit offerte, soit refusée avec son motif. Une
 * action absente se lit « la fonction n'existe pas dans ce produit » — c'est
 * le défaut déjà payé ici par la légende « bâti — non rattaché » qui annonçait
 * une classe jamais dessinée, et par le basculeur de plan qui ne disait pas
 * pourquoi il ne s'offrait pas.
 *
 * ⚠️ ET LE REFUS NOMME LE REMÈDE. Le modèle est déjà dans ce dépôt :
 * `REFUS_ACTIVATION.derive_bati` ne dit pas « impossible », il dit « un levé
 * contradictoire la rend opposable ». Un refus qui n'indique pas la suite
 * laisse l'agent devant un mur ; un refus qui la nomme est une instruction.
 *
 * ⚠️ AUCUNE ACTION INVENTÉE. Chaque `permission` ci-dessous existe dans
 * `sec.role_permission` (vérifié le 22/09/2026), et chaque action correspond à
 * une route réelle. Un couple ressource/opération absent du catalogue ne lève
 * AUCUNE erreur : `autoriser` rend simplement « non accordé », le bouton reste
 * fermé POUR TOUJOURS, et son motif accuse l'habilitation de l'agent au lieu de
 * la faute de frappe. `fon.attestation` et `fon.revendication` n'existent pas
 * au catalogue : il n'y a donc pas d'action dessus, malgré les routes.
 */

/** Ce que l'écran sait de la parcelle au moment de décider. */
export interface EtatParcelle {
  statut: string;
  origine: string | null;
  /** Combien de droits inscrits. Zéro ⇒ il n'y a rien à transférer. */
  droits: number;
  /** Combien de levés déposés. */
  releves: number;
  verifiee: boolean;
}

export interface ActionParcelle {
  readonly cle: string;
  readonly libelle: string;
  readonly aide: string;
  /** La rubrique qu'elle ouvre — confrontée au rail du mode par la garde. */
  readonly rubrique: string;
  readonly permission: { readonly ressource: string; readonly operation: string };
  /** Les métiers dont c'est le ressort. */
  readonly metiers: readonly string[];
  /**
   * Rend `null` quand l'objet s'y prête, sinon LA PHRASE qui dit pourquoi et
   * ce qu'il faut faire d'abord. Jamais « impossible » tout court.
   */
  empechement(p: EtatParcelle): string | null;
}

/** Un levé est le geste qui débloque tout le reste : il n'a pas d'empêchement. */
const TOUJOURS = (): null => null;

export const ACTIONS_PARCELLE: readonly ActionParcelle[] = [
  {
    cle: 'deposer_leve',
    libelle: 'Déposer un levé',
    aide: 'un levé contradictoire rend la parcelle opposable',
    rubrique: 'geometre',
    permission: { ressource: 'cad.releve', operation: 'creer' },
    metiers: ['geometre'],
    /* ⚠️ SANS EMPÊCHEMENT, ET C'EST VOULU. C'est l'action qui sort une parcelle
     * de l'échafaudage ; la bloquer sur l'état de l'objet fermerait la seule
     * porte de sortie des 1,76 million de parcelles dérivées du bâti. */
    empechement: TOUJOURS,
  },
  {
    cle: 'corriger_emprise',
    libelle: 'Corriger l’emprise (atelier)',
    aide: 'reprendre le contour dans l’atelier SIG — mitoyennetés comprises',
    rubrique: 'atelier',
    permission: { ressource: 'cad.parcelle', operation: 'modifier' },
    metiers: ['geometre', 'etat'],
    /* ⚠️ SANS EMPÊCHEMENT D'OBJET : c'est le SERVICE qui refuse une correction
     * (projet seul, droits actifs, origine qui ne situe pas sans titrer —
     * `refusDeCorrection`), et l'atelier affiche son refus tel quel. Répéter
     * la règle ici en ferait une seconde implémentation. */
    empechement: TOUJOURS,
  },
  {
    cle: 'mettre_au_plan',
    libelle: 'Mettre au plan cadastral',
    aide: 'inscrire la parcelle : elle devient opposable',
    rubrique: 'carte',
    permission: { ressource: 'cad.parcelle', operation: 'modifier' },
    metiers: ['geometre', 'collectivite', 'etat'],
    empechement: (p) => {
      if (p.statut === 'active') return 'Déjà inscrite au plan cadastral : elle est opposable.';
      /* Le domaine sait déjà refuser ET nommer le remède — on ne réécrit pas
       * sa phrase, on la sert. Deux formulations de la même règle divergent au
       * premier correctif appliqué à une seule. */
      if (estEchafaudage(p.origine)) return REFUS_ACTIVATION[p.origine as OrigineEchafaudage];
      if (p.releves === 0) {
        return 'Aucun levé déposé sur cette parcelle : rien ne fonde encore son contour. '
             + 'Un levé doit précéder la mise au plan.';
      }
      return null;
    },
  },
  {
    cle: 'ouvrir_mutation',
    libelle: 'Ouvrir une mutation',
    aide: 'transférer un droit inscrit sur ce fonds',
    rubrique: 'mutations',
    permission: { ressource: 'fon.mutation', operation: 'creer' },
    metiers: ['collectivite', 'etat'],
    empechement: (p) => {
      /* ⚠️ LE CAS DE LA CAPTURE DU FONDATEUR : GN-PAR-000286181 porte ZÉRO
       * droit. Offrir « muter » y produirait un clic refusé côté serveur, et
       * un bouton refusé enseigne que le produit est cassé. Le registre ne
       * rattache encore cette parcelle à personne : il n'y a rien à transférer. */
      if (p.droits === 0) {
        return 'Aucun droit inscrit sur ce fonds : il n’y a rien à transférer. '
             + 'Une mutation suppose un titulaire — commencez par le levé et l’inscription.';
      }
      if (p.statut !== 'active') {
        return 'Cette parcelle n’est pas inscrite au plan : une mutation porterait sur un '
             + 'fonds non opposable. Mettez-la au plan d’abord.';
      }
      return null;
    },
  },
  {
    cle: 'ouvrir_demande',
    libelle: 'Ouvrir une demande au guichet',
    aide: 'saisir le guichet foncier sur ce fonds',
    rubrique: 'guichet',
    permission: { ressource: 'fon.demande', operation: 'creer' },
    metiers: ['collectivite', 'urbanisme', 'etat'],
    /* Une demande se dépose sur n'importe quel fonds : c'est précisément la
     * porte d'entrée de ceux qui ne sont encore rien. */
    empechement: TOUJOURS,
  },
  {
    cle: 'rattacher_fiscal',
    libelle: 'Rattacher à un objet fiscal',
    aide: 'lier ce fonds au registre de l’impôt',
    rubrique: 'foncier',
    permission: { ressource: 'lien.objet_fiscal', operation: 'creer' },
    metiers: ['fiscal', 'etat'],
    empechement: (p) => {
      if (p.statut !== 'active') {
        return 'Le fonds n’est pas inscrit au plan cadastral. L’impôt n’assoit que sur '
             + 'de l’opposable : la mise au plan doit précéder le rattachement.';
      }
      return null;
    },
  },
  {
    cle: 'consulter_controles',
    libelle: 'Consulter les contrôles',
    aide: 'ce que le contrôle qualité a relevé sur ce fonds',
    rubrique: 'anomalies',
    permission: { ressource: 'cad.controle', operation: 'lire' },
    metiers: ['geometre', 'etat'],
    empechement: TOUJOURS,
  },
  {
    cle: 'ecrire_courrier',
    libelle: 'Écrire un courrier',
    aide: 'notifier une partie, opposablement',
    rubrique: 'courrier',
    permission: { ressource: 'doc.document', operation: 'creer' },
    metiers: ['collectivite', 'etat'],
    empechement: TOUJOURS,
  },
];

export type Verdict =
  | { readonly offerte: true; readonly action: ActionParcelle }
  | {
      readonly offerte: false;
      /** `objet` : la parcelle ne s'y prête pas. `habilitation` : le rôle ne l'a pas. */
      readonly cause: 'objet' | 'habilitation';
      readonly motif: string;
      readonly action: ActionParcelle;
    };

/**
 * Les actions du ressort de ce métier, chacune avec son verdict.
 *
 * ⚠️ ON NE FILTRE PAS SUR L'HABILITATION, ON LA DIT. Retirer les actions qu'on
 * n'a pas le droit de faire produit un écran qui varie d'un agent à l'autre
 * sans que personne ne sache pourquoi — et un agent qui ne voit pas une action
 * ne demande jamais l'habilitation qui la débloque.
 *
 * ⚠️ L'OBJET PASSE AVANT L'HABILITATION. Dire « votre rôle ne l'autorise pas »
 * sur une parcelle qui de toute façon ne s'y prête pas envoie demander une
 * habilitation inutile. Le premier obstacle est celui qu'on annonce.
 */
export function actionsPour(
  parcelle: EtatParcelle,
  metier: string | null,
  permissions: readonly { ressource: string; operation: string }[],
): Verdict[] {
  const aLaPermission = (a: ActionParcelle): boolean =>
    permissions.some((p) => p.ressource === a.permission.ressource
                         && p.operation === a.permission.operation);

  return ACTIONS_PARCELLE
    .filter((a) => metier !== null && a.metiers.includes(metier))
    .map((action): Verdict => {
      const bloc = action.empechement(parcelle);
      if (bloc !== null) return { offerte: false, cause: 'objet', motif: bloc, action };
      if (!aLaPermission(action)) {
        return {
          offerte: false, cause: 'habilitation', action,
          motif: `Votre rôle n’a pas la permission ${action.permission.ressource}`
               + `:${action.permission.operation}.`,
        };
      }
      return { offerte: true, action };
    });
}

/**
 * Ce qu'on affiche quand le métier n'ouvre AUCUNE action sur ce fonds.
 * ⚠️ Une liste vide sans phrase se lit « l'onglet est cassé ».
 */
export function motifListeVide(metier: string | null): string {
  if (metier === null) {
    return 'Aucun métier actif : choisissez votre mode pour voir ce que vous pouvez faire ici.';
  }
  return 'Votre métier n’ouvre aucune action sur une parcelle. '
       + 'Ce n’est pas un défaut d’affichage : ses gestes portent sur d’autres objets.';
}
